/**
 * CallFlow Detail Component
 *
 * Detailed view of a single CallFlow with tabs for overview, leads, phones, logs, and settings.
 */

import { useEffect, useState } from 'react';
import { callFlowService, CallFlow, CallFlowStats } from '@/services/callFlowService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import {
  Play,
  Pause,
  ArrowLeft,
  Loader2,
  RefreshCw,
  Activity,
} from 'lucide-react';
import { CallFlowLeadsTab } from './CallFlowLeadsTab';
import { CallFlowPhonesTab } from './CallFlowPhonesTab';
import { CallFlowLogsTab } from './CallFlowLogsTab';
import { CallFlowSettingsTab } from './CallFlowSettingsTab';
import { CallFlowDebugTab } from './CallFlowDebugTab';
import { CallFlowQueueMonitor } from '../campaigns/CallFlowQueueMonitor';

interface CallFlowDetailProps {
  callflowId: string;
  organizationId: string;
  onBack: () => void;
}

export function CallFlowDetail({ callflowId, organizationId, onBack }: CallFlowDetailProps) {
  const [callflow, setCallflow] = useState<CallFlow | null>(null);
  const [stats, setStats] = useState<CallFlowStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCallFlow();
    fetchStats();

    // Poll stats every 5 seconds when active
    const interval = setInterval(() => {
      if (callflow?.status === 'active') {
        fetchStats();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [callflowId, callflow?.status]);

  const fetchCallFlow = async () => {
    try {
      setIsLoading(true);
      const data = await callFlowService.getCallFlow(organizationId, callflowId);
      setCallflow(data);
    } catch (error) {
      console.error('Failed to fetch CallFlow:', error);
      toast({
        title: 'Error',
        description: 'Failed to load CallFlow details',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await callFlowService.getStats(callflowId);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleStart = async () => {
    try {
      setActionLoading(true);
      await callFlowService.startCallFlow(callflowId);
      toast({
        title: 'Success',
        description: 'CallFlow has been queued for execution.',
      });
      await fetchCallFlow();
      await fetchStats();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    try {
      setActionLoading(true);
      await callFlowService.pauseCallFlow(callflowId);
      toast({
        title: 'Success',
        description: 'CallFlow is pausing. Active calls will complete gracefully.',
      });
      await fetchCallFlow();
      await fetchStats();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to pause CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    try {
      setActionLoading(true);
      await callFlowService.resumeCallFlow(callflowId);
      toast({
        title: 'Success',
        description: 'CallFlow has been queued to resume.',
      });
      await fetchCallFlow();
      await fetchStats();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resume CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      completed_with_failures: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
    };

    return (
      <Badge className={colors[status]}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  if (isLoading || !callflow) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const completionPercentage = stats?.completion_percentage || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{callflow.name}</h1>
              {getStatusBadge(callflow.status)}
            </div>
            {callflow.description && (
              <p className="text-gray-600 mt-1">{callflow.description}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => {
            fetchCallFlow();
            fetchStats();
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>

          {['draft', 'paused'].includes(callflow.status) && (
            <Button onClick={handleStart} disabled={actionLoading}>
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {callflow.status === 'paused' ? 'Resume' : 'Start'}
            </Button>
          )}

          {callflow.status === 'active' && (
            <Button variant="outline" onClick={handlePause} disabled={actionLoading}>
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Pause className="h-4 w-4 mr-2" />
              )}
              Pause
            </Button>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats?.total_leads || callflow.total_leads}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">
              {stats?.calls_completed || callflow.calls_completed}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">
              {stats?.calls_failed || callflow.calls_failed}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">
              {stats?.calls_in_progress || callflow.calls_in_progress}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Progress</CardTitle>
            <span className="text-2xl font-bold">{completionPercentage.toFixed(1)}%</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>

          {stats?.is_running && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <Activity className="h-4 w-4 text-green-600 animate-pulse" />
              <span>Worker is active</span>
              {stats.worker_id && (
                <span className="text-xs text-gray-500">({stats.worker_id})</span>
              )}
              {stats.last_heartbeat && (
                <span className="text-xs text-gray-500">
                  Last heartbeat: {new Date(stats.last_heartbeat).toLocaleTimeString()}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="queue">Queue Monitor</TabsTrigger>
          <TabsTrigger value="phones">Phone Numbers</TabsTrigger>
          <TabsTrigger value="debug">Debug</TabsTrigger>
          <TabsTrigger value="logs">Execution Logs</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>CallFlow Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Rate Limit</p>
                  <p className="font-medium">
                    {callflow.max_calls_per_second_per_number} calls/second per phone
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Max Concurrent</p>
                  <p className="font-medium">{callflow.max_concurrent_calls} calls</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Retry Delays</p>
                  <p className="font-medium">
                    {callflow.retry_delays.join(', ')} minutes
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">
                    {new Date(callflow.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads">
          <CallFlowLeadsTab callflowId={callflowId} organizationId={organizationId} callflowStatus={callflow.status} />
        </TabsContent>

        <TabsContent value="queue">
          <CallFlowQueueMonitor callflowId={callflowId} />
        </TabsContent>

        <TabsContent value="phones">
          <CallFlowPhonesTab callflowId={callflowId} organizationId={organizationId} callflowStatus={callflow.status} />
        </TabsContent>

        <TabsContent value="debug">
          <CallFlowDebugTab callflowId={callflowId} organizationId={organizationId} />
        </TabsContent>

        <TabsContent value="logs">
          <CallFlowLogsTab callflowId={callflowId} organizationId={organizationId} />
        </TabsContent>

        <TabsContent value="settings">
          <CallFlowSettingsTab
            callflow={callflow}
            organizationId={organizationId}
            onUpdate={fetchCallFlow}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
